import { z } from 'zod';

/**
 * `audit.activity_log` projection for the team / project activity feed UI.
 *
 * The row shape mirrors the DB columns (see `apps/api/src/db/schema/audit/activity-log.ts`)
 * but trims ip_address + user_agent — those are forensic-only fields that
 * the UI never surfaces and shouldn't leak to non-admin viewers.
 *
 * `action` is free text by convention `"<resource>.<verb>"`.  The FE keeps
 * a switch on known action strings for icon + sentence; unknown actions
 * fall through to a generic "X did Y on Z" row.
 */
export const ActivityLogEntrySchema = z.object({
  /** bigserial column — string on the wire because JS lacks bigint by default. */
  id: z.string(),
  actorUserId: z.string().uuid().nullable(),
  /** Resolved on the server for convenient render — null when actor is a system row. */
  actorName: z.string().nullable(),
  actorUsername: z.string().nullable(),
  actorAvatarUrl: z.string().nullable(),
  teamId: z.string().uuid().nullable(),
  action: z.string(),
  resourceType: z.string().nullable(),
  resourceId: z.string().uuid().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string().datetime(),
});
export type ActivityLogEntry = z.infer<typeof ActivityLogEntrySchema>;

export const ActivityLogPageSchema = z.object({
  items: z.array(ActivityLogEntrySchema),
  /** Bigserial id of the last row in the page — pass as `?cursor=` for the next page.
   *  Null when end of feed reached. */
  nextCursor: z.string().nullable(),
});
export type ActivityLogPage = z.infer<typeof ActivityLogPageSchema>;

export const ActivityLogQuerySchema = z.object({
  /** Filter by actor user id.  Optional; default = all actors in the team. */
  actor: z.string().uuid().optional(),
  /** Substring match against `action` text (e.g. `team.member` matches all member events).
   *  Optional; default = all actions. */
  action: z.string().min(1).max(64).optional(),
  /** Project filter — only rows where `payload.projectId === projectId` OR
   *  `resourceType='project' AND resourceId=projectId`.  Used by the project
   *  activity feed; ignored on team-level feed. */
  projectId: z.string().uuid().optional(),
  /** Cursor: bigserial id from the previous page.  Rows with id < cursor are returned. */
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ActivityLogQuery = z.infer<typeof ActivityLogQuerySchema>;
